import {
  ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { graphql, HeadFC } from 'gatsby'
import { navigate } from 'gatsby-plugin-intl'
import useEmblaCarousel from 'embla-carousel-react'
import Autoplay from 'embla-carousel-autoplay'

import Link from '@atoms/Link'
import MicrocmsImage from '@atoms/MicrocmsImage'
import Button from '@atoms/Button'
import ArticleItem from '@src/components/molecules/ArticleItem'
import VideoItem from '@molecules/VideoItem'
import PauseIcon from '@icons/Pause'
import PlayIcon from '@icons/Play'
import Layout from '@src/components/Layout'
import Seo from '@src/components/Seo'
import VideoModal from '@src/components/VideoModal'
import Autocomplete from '@src/components/Autocomplete'
import CustomInstantSearch from '@src/components/CustomInstantSearch'
import { useCarouselButton } from '@src/components/Artisan/CarouselButton'
import RequestForm from '@src/components/RequestForm'
import {
  Article,
  CATEGORIES,
  Category,
  useAllArticles,
  useArticle,
} from '@models/Article'
import { Parent } from '@models/Parent'
import { useTranslation } from '@models/Translation'
import { Video } from '@src/models/Video'
import {
  convertPublishDateForDate,
  removeHtmlTags,
  useWindowSize,
} from '@models/Util'
import { StoreContext } from '@src/models/Store'
import { checkMaxAudioCount } from '@models/Voice'
import dayjsUtil from '@models/DayJS'

import * as style from './style.module.sass'
import artisanMainImage from './assets/artisan-main.webp'
import requestMainImage from './assets/request-main.webp'

type IndexPageProps = {
  data: {
    allParent: {
      nodes: Parent[]
    }
    allEnglishParent: {
      nodes: Parent[]
    }
    allVideo: {
      nodes: Video[]
    }
    allTopInfo: {
      nodes: {
        dailyRanking: string[]
        dailyRankingEn: string[]
        weeklyRanking: string[]
        weeklyRankingEn: string[]
        popularTags: string[]
        popularTagsEn: string[]
        searchTags: string[]
        searchTagsEn: string[]
      }[]
    }
  }
}

type Tab = {
  width: number
  left: number
  tabLeft: number
}
const IndexPage = ({ data }: IndexPageProps) => {
  const { entity, action } = useContext(StoreContext)
  const { locale, t } = useTranslation()
  const allArticles = useAllArticles()
  const { getArticles } = useArticle()
  const [willDisplayInfo, setWillDisplayInfo] = useState(false)
  const allParents =
    locale === 'en' ? data.allEnglishParent.nodes : data.allParent.nodes
  const { width: windowWidth } = useWindowSize()
  const isPlaying = entity.voice.isPlaying && entity.voice.context === 'latest'

  const now = dayjsUtil()
  const [loading, setLoading] = useState(true)
  const [selectedTab, setSelectedTab] = useState<Tab>({
    width: 160,
    left: 340,
    tabLeft: 0,
  })
  const tabList = [
    'request',
    'series',
    'feature',
    'artisan',
    'latest',
    'dailyRanking',
    'weeklyRanking',
    'video',
    'event',
  ]
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      startIndex: locale === 'ja' ? 4 : 3,
    },
    [Autoplay({ delay: 8000 })],
  )
  const { selectedIndex, onCarouselButtonClick } = useCarouselButton(emblaApi)
  const [showRequestForm, setShowRequestForm] = useState(false)

  const [pickupCategory, setPickupCategory] = useState<Category>(
    allArticles[0].data.category,
  )

  const dailyRankingArticles = getArticles(
    locale === 'en'
      ? data.allTopInfo.nodes[0].dailyRankingEn
      : data.allTopInfo.nodes[0].dailyRanking,
  ).slice(0, 5)
  const weeklyRankingArticles = getArticles(
    locale === 'en'
      ? data.allTopInfo.nodes[0].weeklyRankingEn
      : data.allTopInfo.nodes[0].weeklyRanking,
  ).slice(0, 5)
  const popularTags =
    locale === 'en'
      ? data.allTopInfo.nodes[0].popularTagsEn
      : data.allTopInfo.nodes[0].popularTags
  const searchTags =
    locale === 'en'
      ? data.allTopInfo.nodes[0].searchTagsEn
      : data.allTopInfo.nodes[0].searchTags

  const latestArticles = allArticles.slice(0, 18)

  const allSeriesArticles = allArticles.filter(
    article => article.data.category === 'series',
  )
  const recentSeries = allParents.find(
    parent => parent.pageId === allSeriesArticles[0].parentPage?.id,
  )
  const recentSeriesArticles = allSeriesArticles
    .filter(article => article.parentPage?.id === recentSeries?.pageId)
    .slice(0, 4)

  const featuresArticles = allArticles.filter(
    article => article.data.category === 'features',
  )
  const recentFeature = allParents.find(
    parent => parent.pageId === featuresArticles[0].parentPage?.id,
  )
  const recentFeatureArticles = featuresArticles
    .filter(article => article.parentPage?.id === recentFeature?.pageId)
    .slice(0, 4)

  const artisanArticles = allArticles
    .filter(article => article.isArtisan)
    .slice(0, 4)

  const eventsArticles = allArticles
    .filter(article => article.data.category === 'events')
    .slice(0, 5)

  const requestArticles = allArticles
    .filter(article => typeof article.data.requester === 'string')
    .slice(0, 4)

  const infoArticles = allArticles
    .filter(article => article.data.category === 'info')
    .slice(0, 4)

  const allVideos = data.allVideo.nodes.filter(
    video => video.category !== 'Shorts',
  )
  const [selectedVideoIndex, setSelectedVideoIndex] = useState<
    number | undefined
  >()

  const displayInfo = useMemo(() => {
    const now = new Date()
    const threeMonthsAgo = new Date(new Date().setMonth(now.getMonth() - 3))
    return infoArticles.some(
      article =>
        new Date(convertPublishDateForDate(article.publishDate)) >
        threeMonthsAgo,
    )
  }, [infoArticles])

  const playAll = () => {
    if (entity.user?.isSignIn !== true) {
      if (checkMaxAudioCount()) {
        navigate('/signup?limit=voice')
        return
      }
    }

    if (entity.voice.context === 'latest') {
      action.setVoiceIsPlaying(true)
      return
    }

    action.setVoiceArticleIds([], 'latest')
  }

  const updateTab = () => {
    const target = document.querySelector(
      'button[data-active="true"]',
    ) as HTMLButtonElement | null
    if (target === null) {
      return
    }

    const moveTab = windowWidth <= 960
    const tabWidth = target.offsetWidth

    if (moveTab) {
      const centerOffset = windowWidth / 2
      const margin = windowWidth <= 600 ? 16 : 40
      setSelectedTab({
        width: target.offsetWidth,
        left: target.offsetLeft,
        tabLeft: -target.offsetLeft + centerOffset - margin - tabWidth / 2,
      })
    } else {
      setSelectedTab({
        width: target.offsetWidth,
        left: target.offsetLeft,
        tabLeft: 0,
      })
    }

    setTimeout(() => setLoading(false), 1000)
  }

  useEffect(updateTab, [windowWidth, selectedIndex])

  useLayoutEffect(() => {
    setWillDisplayInfo(displayInfo)
  }, [displayInfo])

  return (
    <Layout>
      <div className={style.top}>
        <div className={style.searchAndTag}>
          <div className={style.search}>
            <CustomInstantSearch enablesRouting={false}>
              <Autocomplete
                handleQueryChange={(query: string) =>
                  navigate(`/search?q=${query}`)
                }
                placeholder={`${searchTags[0]}, ${searchTags[2]}`}
              />
            </CustomInstantSearch>
          </div>
          <div className={style.date}>
            {now.format('MM.DD')}
            <span>{now.format('ddd').toUpperCase()}</span>
          </div>
          <div className={style.tags}>
            {popularTags.map(tag => (
              <Link
                to={`/search?q=${tag}&ref=tag`}
                className={style.tag}
                key={tag}
              >
                #{tag}
              </Link>
            ))}
          </div>
        </div>

        <div className={style.topWrapper}>
          <div className={style.leftWrapper}>
            <section className={style.tabSection} data-show={!loading}>
              <ul
                className={style.tabs}
                style={{ transform: `translateX(${selectedTab.tabLeft}px)` }}
              >
                {tabList.slice(locale === 'ja' ? 0 : 1).map((tab, index) => (
                  <li key={tab}>
                    <button
                      onClick={() => {
                        onCarouselButtonClick(index)
                        emblaApi?.plugins().autoplay?.stop()
                      }}
                      data-active={selectedIndex === index}
                      data-index={index}
                    >
                      {t(`top.${tab}`)}
                    </button>
                  </li>
                ))}
                <div
                  className={style.marker}
                  style={{
                    width: `${selectedTab.width}px`,
                    transform: `translateX(${selectedTab.left}px)`,
                  }}
                />
              </ul>
              <div ref={emblaRef} className={style.carousel}>
                <div className={style.tabContent}>
                  {locale === 'ja' && (
                    <TabContent
                      articles={requestArticles}
                      link={{
                        to: '/request',
                        text: 'リクエスト記事をもっと見る',
                      }}
                    >
                      <div className={style.mainRequest}>
                        <img src={requestMainImage} alt="Request" />
                        <div className={style.description}>
                          読者からのリクエストをもとに記事を作成します
                        </div>
                        <div className={style.buttonContainer}>
                          <Button
                            onClick={() => setShowRequestForm(true)}
                            size="medium"
                          >
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M8.5 20.7422C8.29688 20.7422 8.13542 20.6719 8.01562 20.5312C7.90104 20.3958 7.84375 20.2109 7.84375 19.9766V17.8672H7.33594C6.63281 17.8672 6.03385 17.737 5.53906 17.4766C5.04427 17.2161 4.66667 16.8385 4.40625 16.3438C4.15104 15.849 4.02344 15.2526 4.02344 14.5547V8.27344C4.02344 7.57031 4.15104 6.97396 4.40625 6.48438C4.66667 5.98958 5.04427 5.61198 5.53906 5.35156C6.03385 5.08594 6.63281 4.95312 7.33594 4.95312H17.6641C18.3672 4.95312 18.9661 5.08594 19.4609 5.35156C19.9557 5.61198 20.3307 5.98958 20.5859 6.48438C20.8464 6.97917 20.9766 7.57552 20.9766 8.27344V14.5547C20.9766 15.2526 20.8464 15.849 20.5859 16.3438C20.3307 16.8333 19.9557 17.2109 19.4609 17.4766C18.9661 17.737 18.3672 17.8672 17.6641 17.8672H12.1016L9.35938 20.2812C9.1875 20.4323 9.03906 20.5469 8.91406 20.625C8.78906 20.7031 8.65104 20.7422 8.5 20.7422ZM8.74219 19.6562L11.3125 17.2188C11.4427 17.0938 11.5625 17.013 11.6719 16.9766C11.7812 16.9349 11.9245 16.9141 12.1016 16.9141H17.6562C18.4219 16.9141 19.0052 16.7083 19.4062 16.2969C19.8125 15.8854 20.0156 15.3021 20.0156 14.5469V8.27344C20.0156 7.52344 19.8125 6.94531 19.4062 6.53906C19.0052 6.1276 18.4219 5.92188 17.6562 5.92188H7.34375C6.57292 5.92188 5.98698 6.1276 5.58594 6.53906C5.1849 6.94531 4.98438 7.52344 4.98438 8.27344V14.5469C4.98438 15.3021 5.1849 15.8854 5.58594 16.2969C5.98698 16.7083 6.57292 16.9141 7.34375 16.9141H8.28906C8.45052 16.9141 8.5651 16.9479 8.63281 17.0156C8.70573 17.0833 8.74219 17.1979 8.74219 17.3594V19.6562ZM8.95312 11.4141C8.95312 11.263 9 11.1406 9.09375 11.0469C9.1875 10.9531 9.3125 10.9062 9.46875 10.9062H12V8.36719C12 8.21615 12.0469 8.09375 12.1406 8C12.2344 7.90104 12.3516 7.85156 12.4922 7.85156C12.6432 7.85156 12.7656 7.89844 12.8594 7.99219C12.9531 8.08594 13 8.21094 13 8.36719V10.9062H15.5469C15.6927 10.9062 15.8125 10.9531 15.9062 11.0469C16 11.1406 16.0469 11.263 16.0469 11.4141C16.0469 11.5547 16 11.6719 15.9062 11.7656C15.8125 11.8542 15.6927 11.8984 15.5469 11.8984H13V14.4453C13 14.5964 12.9531 14.7188 12.8594 14.8125C12.7656 14.9062 12.6432 14.9531 12.4922 14.9531C12.3516 14.9531 12.2344 14.9062 12.1406 14.8125C12.0469 14.7135 12 14.5911 12 14.4453V11.8984H9.46875C9.31771 11.8984 9.19271 11.8542 9.09375 11.7656C9 11.6719 8.95312 11.5547 8.95312 11.4141Z"
                                fill="currentColor"
                              />
                            </svg>
                            取材&amp;記事リクエストはこちら
                          </Button>
                        </div>
                      </div>
                    </TabContent>
                  )}
                  <TabContent
                    articles={recentSeriesArticles}
                    link={{ to: '/series', text: t('top.allSeries') }}
                  >
                    {recentSeries && (
                      <Link to={recentSeries.path} className={style.mainSeries}>
                        <div className={style.imageContainer}>
                          <MicrocmsImage
                            image={recentSeries.image}
                            alt={recentSeries.title}
                            type="hero"
                            disableAspectRatio
                          />
                        </div>
                        <div className={style.title}>
                          {removeHtmlTags(recentSeries.title)}
                        </div>
                        <div className={style.description}>
                          {removeHtmlTags(recentSeries.contents)}
                        </div>
                      </Link>
                    )}
                  </TabContent>
                  <TabContent
                    articles={recentFeatureArticles}
                    link={{ to: '/features', text: t('top.allFeatures') }}
                  >
                    {recentFeature && (
                      <Link
                        to={recentFeature.path}
                        className={style.mainFeature}
                      >
                        <div className={style.idContainer}>
                          <div className={style.id}>
                            <div>FEATURES</div>
                            <span>#</span>
                            {recentFeature.pageId.slice(-3)}
                          </div>
                          <div className={style.imageContainer}>
                            <MicrocmsImage
                              image={recentFeature.image}
                              alt={recentFeature.title}
                              type="hero"
                              disableAspectRatio
                            />
                          </div>
                        </div>
                        <div className={style.title}>
                          {removeHtmlTags(recentFeature.title)}
                        </div>
                        <div className={style.description}>
                          {removeHtmlTags(recentFeature.contents)}
                        </div>
                      </Link>
                    )}
                  </TabContent>
                  <TabContent
                    articles={artisanArticles}
                    link={{ to: '/artisan', text: t('top.toArtisan') }}
                  >
                    <Link to="/artisan" className={style.mainArtisan}>
                      <img src={artisanMainImage} alt="Artisan" />
                      <div className={style.message}>
                        The New in Old.<span>{t('artisanAbout.subTitle')}</span>
                      </div>
                      <div
                        className={style.description}
                        dangerouslySetInnerHTML={{
                          __html: t('top.artisanDescription'),
                        }}
                      />
                    </Link>
                  </TabContent>
                  <TabContent
                    articles={latestArticles.slice(1, 5)}
                    link={{ to: '/all', text: t('top.allArticle') }}
                  >
                    <div className={style.rankingContainer}>
                      <ArticleItem
                        article={latestArticles[0]}
                        type="large"
                        listRule="no-list"
                      />
                    </div>
                  </TabContent>
                  <TabContent articles={dailyRankingArticles.slice(1)} withRank>
                    <div className={style.rankingContainer}>
                      <ArticleItem
                        article={dailyRankingArticles[0]}
                        type="large"
                        listRule="no-list"
                        rank={1}
                      />
                    </div>
                  </TabContent>
                  <TabContent
                    articles={weeklyRankingArticles.slice(1)}
                    withRank
                  >
                    <div className={style.rankingContainer}>
                      <ArticleItem
                        article={weeklyRankingArticles[0]}
                        type="large"
                        listRule="no-list"
                        rank={1}
                      />
                    </div>
                  </TabContent>
                  <div className={style.tabContentItem}>
                    <button
                      className={style.mainVideo}
                      key={allVideos[0].videoId}
                      onClick={() => setSelectedVideoIndex(0)}
                      style={{
                        backgroundImage: `url(${`https://i.ytimg.com/vi/${allVideos[0].videoId}/maxresdefault.jpg`})`,
                      }}
                    >
                      <div className={style.title}>
                        {locale === 'ja'
                          ? allVideos[0].title
                          : allVideos[0].titleEn}
                      </div>
                      <div className={style.tags}>
                        {(locale === 'ja'
                          ? allVideos[0].tags
                          : allVideos[0].tagsEn
                        ).map(tag => (
                          <span className={style.tag} key={tag}>
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </button>
                    <div className={style.videos}>
                      {allVideos.slice(1, 5).map((video, index) => (
                        <VideoItem
                          video={video}
                          onClick={() => setSelectedVideoIndex(index + 1)}
                          listRule="pc"
                          key={video.videoId}
                        />
                      ))}
                    </div>
                    <Link to="/videos" className={style.more}>
                      <Button design="border" size="large" icon="arrow">
                        {t('top.allVideos')}
                      </Button>
                    </Link>
                  </div>
                  <TabContent
                    articles={eventsArticles.slice(1)}
                    link={{ to: '/events', text: t('top.allEvents') }}
                  >
                    <ArticleItem
                      article={eventsArticles[0]}
                      type="large"
                      listRule="no-list"
                    />
                  </TabContent>
                </div>

                <button
                  className={style.ui}
                  onClick={() => emblaApi?.scrollNext()}
                >
                  <svg width="15" height="12" viewBox="0 0 15 12" fill="none">
                    <path
                      d="M14.2578 5.65137C14.2578 5.83691 14.1846 6.00049 14.0381 6.14209L9.16748 10.998C9.02588 11.1348 8.86719 11.2031 8.69141 11.2031C8.51074 11.2031 8.35938 11.1421 8.2373 11.02C8.11523 10.9028 8.0542 10.7539 8.0542 10.5732C8.0542 10.4854 8.06885 10.4023 8.09814 10.3242C8.12744 10.2412 8.17139 10.1704 8.22998 10.1118L9.87061 8.44189L12.7783 5.79785L12.9248 6.15674L10.5664 6.30322H1.30127C1.11084 6.30322 0.95459 6.24219 0.83252 6.12012C0.715332 5.99805 0.656738 5.8418 0.656738 5.65137C0.656738 5.46094 0.715332 5.30469 0.83252 5.18262C0.95459 5.06055 1.11084 4.99951 1.30127 4.99951H10.5664L12.9248 5.146L12.7783 5.51221L9.87061 2.86084L8.22998 1.19092C8.17139 1.13232 8.12744 1.06396 8.09814 0.98584C8.06885 0.902832 8.0542 0.817383 8.0542 0.729492C8.0542 0.548828 8.11523 0.399902 8.2373 0.282715C8.35938 0.160645 8.51074 0.0996094 8.69141 0.0996094C8.7793 0.0996094 8.8623 0.116699 8.94043 0.150879C9.02344 0.185059 9.104 0.241211 9.18213 0.319336L14.0381 5.16064C14.1846 5.30225 14.2578 5.46582 14.2578 5.65137Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
                <button
                  className={style.ui}
                  onClick={() => emblaApi?.scrollPrev()}
                >
                  <svg width="15" height="12" viewBox="0 0 15 12" fill="none">
                    <path
                      d="M14.2578 5.65137C14.2578 5.83691 14.1846 6.00049 14.0381 6.14209L9.16748 10.998C9.02588 11.1348 8.86719 11.2031 8.69141 11.2031C8.51074 11.2031 8.35938 11.1421 8.2373 11.02C8.11523 10.9028 8.0542 10.7539 8.0542 10.5732C8.0542 10.4854 8.06885 10.4023 8.09814 10.3242C8.12744 10.2412 8.17139 10.1704 8.22998 10.1118L9.87061 8.44189L12.7783 5.79785L12.9248 6.15674L10.5664 6.30322H1.30127C1.11084 6.30322 0.95459 6.24219 0.83252 6.12012C0.715332 5.99805 0.656738 5.8418 0.656738 5.65137C0.656738 5.46094 0.715332 5.30469 0.83252 5.18262C0.95459 5.06055 1.11084 4.99951 1.30127 4.99951H10.5664L12.9248 5.146L12.7783 5.51221L9.87061 2.86084L8.22998 1.19092C8.17139 1.13232 8.12744 1.06396 8.09814 0.98584C8.06885 0.902832 8.0542 0.817383 8.0542 0.729492C8.0542 0.548828 8.11523 0.399902 8.2373 0.282715C8.35938 0.160645 8.51074 0.0996094 8.69141 0.0996094C8.7793 0.0996094 8.8623 0.116699 8.94043 0.150879C9.02344 0.185059 9.104 0.241211 9.18213 0.319336L14.0381 5.16064C14.1846 5.30225 14.2578 5.46582 14.2578 5.65137Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </div>
            </section>

            <section className={style.latestSection}>
              <Link className={style.label} to="/all">
                {t('top.latestArticles')}
                <svg width="9" height="16" viewBox="0 0 9 16" fill="none">
                  <path
                    d="M8.74805 8.29639C8.74805 8.57861 8.64844 8.81934 8.42432 9.03516L1.94971 15.3687C1.76709 15.5513 1.54297 15.6426 1.27734 15.6426C0.737793 15.6426 0.306152 15.2275 0.306152 14.6797C0.306152 14.4141 0.422363 14.1733 0.59668 13.9907L6.44043 8.29639L0.59668 2.60205C0.414062 2.41113 0.306152 2.17041 0.306152 1.90479C0.306152 1.36523 0.737793 0.950195 1.27734 0.950195C1.54297 0.950195 1.76709 1.0415 1.94971 1.22412L8.42432 7.55762C8.64014 7.77344 8.74805 8.01416 8.74805 8.29639Z"
                    fill="currentColor"
                  />
                </svg>
              </Link>
              {isPlaying ? (
                <button
                  onClick={() => action.setVoiceIsPlaying(false)}
                  className={style.play}
                  data-show={entity.user !== undefined}
                  data-playing={true}
                >
                  <PauseIcon />
                  <span>{t('voice.stop')}</span>
                </button>
              ) : (
                <button
                  onClick={playAll}
                  className={style.play}
                  data-show={entity.user !== undefined}
                >
                  <PlayIcon />
                  <span>{t('voice.playAll')}</span>
                </button>
              )}
              <div className={style.articles}>
                {latestArticles.map(article => (
                  <ArticleItem
                    article={article}
                    key={article.blogId}
                    listRule="smallTab"
                  />
                ))}
                <Link to="/all" className={style.more}>
                  <Button design="border" size="large" icon="arrow">
                    {t('top.allArticle')}
                  </Button>
                </Link>
              </div>
            </section>
          </div>

          <section className={style.sideSection}>
            <div className={style.label}>{t('top.weeklyRanking')}</div>
            <div className={style.articles}>
              {weeklyRankingArticles.map((article, index) => (
                <ArticleItem
                  article={article}
                  rank={index + 1}
                  key={article.blogId}
                />
              ))}
            </div>

            <Link className={style.label} to="/videos">
              {t('top.video')}
              <svg width="9" height="16" viewBox="0 0 9 16" fill="none">
                <path
                  d="M8.74805 8.29639C8.74805 8.57861 8.64844 8.81934 8.42432 9.03516L1.94971 15.3687C1.76709 15.5513 1.54297 15.6426 1.27734 15.6426C0.737793 15.6426 0.306152 15.2275 0.306152 14.6797C0.306152 14.4141 0.422363 14.1733 0.59668 13.9907L6.44043 8.29639L0.59668 2.60205C0.414062 2.41113 0.306152 2.17041 0.306152 1.90479C0.306152 1.36523 0.737793 0.950195 1.27734 0.950195C1.54297 0.950195 1.76709 1.0415 1.94971 1.22412L8.42432 7.55762C8.64014 7.77344 8.74805 8.01416 8.74805 8.29639Z"
                  fill="currentColor"
                />
              </svg>
            </Link>
            <div className={style.videos}>
              {allVideos.slice(0, 5).map((video, index) => (
                <VideoItem
                  video={video}
                  onClick={() => setSelectedVideoIndex(index)}
                  key={video.videoId}
                />
              ))}
            </div>
          </section>
        </div>

        <section className={style.pickupSection}>
          <div className={style.label}>{t('top.pickup')}</div>
          <ul className={style.categories}>
            {CATEGORIES.map(category => (
              <li key={category}>
                <button
                  onClick={() => {
                    setPickupCategory(category)
                  }}
                  data-active={pickupCategory === category}
                >
                  <span>{category.toUpperCase()}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className={style.list}>
            {allArticles
              .filter(article => article.data.category === pickupCategory)
              .slice(0, 5)
              .map((article, index) => (
                <div className={style.container} key={article.blogId}>
                  <ArticleItem
                    article={article}
                    type={index === 0 ? 'flex' : 'normal'}
                    listRule={index === 0 ? 'no-list' : 'smallTab'}
                  />
                </div>
              ))}
          </div>
          <Link to={`/${pickupCategory}`} className={style.more}>
            <Button design="border" size="large" icon="arrow">
              {locale === 'ja'
                ? `${pickupCategory.toUpperCase()}の特集記事をもっと見る`
                : `${pickupCategory.charAt(0).toUpperCase() + pickupCategory.slice(1)} Articles`}
            </Button>
          </Link>
        </section>

        <section className={style.videoSection} data-only="tab">
          <div className={style.label}>{t('top.video')}</div>
          <div className={style.list}>
            {allVideos.slice(0, 5).map((video, index) => (
              <div className={style.container} key={video.videoId}>
                <VideoItem
                  video={video}
                  onClick={() => setSelectedVideoIndex(index)}
                  listRule={index === 0 ? 'no-list' : 'smallTab'}
                  key={video.videoId}
                />
              </div>
            ))}
          </div>
          <Link to={`/${pickupCategory}`} className={style.more}>
            <Button design="border" size="large" icon="arrow">
              {t('top.allVideos')}
            </Button>
          </Link>
        </section>

        <section className={style.rankingSection} data-only="tab">
          <div className={style.label}>{t('top.weeklyRanking')}</div>
          <div className={style.list}>
            {weeklyRankingArticles.map((article, index) => (
              <div className={style.container} key={article.blogId}>
                <ArticleItem
                  article={article}
                  listRule={index === 0 ? 'no-list' : 'smallTab'}
                  rank={index + 1}
                />
              </div>
            ))}
          </div>
        </section>

        {willDisplayInfo && (
          <section className={style.infoSection}>
            <Link className={style.label} to="/info">
              INFO
              <svg width="9" height="16" viewBox="0 0 9 16" fill="none">
                <path
                  d="M8.74805 8.29639C8.74805 8.57861 8.64844 8.81934 8.42432 9.03516L1.94971 15.3687C1.76709 15.5513 1.54297 15.6426 1.27734 15.6426C0.737793 15.6426 0.306152 15.2275 0.306152 14.6797C0.306152 14.4141 0.422363 14.1733 0.59668 13.9907L6.44043 8.29639L0.59668 2.60205C0.414062 2.41113 0.306152 2.17041 0.306152 1.90479C0.306152 1.36523 0.737793 0.950195 1.27734 0.950195C1.54297 0.950195 1.76709 1.0415 1.94971 1.22412L8.42432 7.55762C8.64014 7.77344 8.74805 8.01416 8.74805 8.29639Z"
                  fill="currentColor"
                />
              </svg>
            </Link>
            <div className={style.articles}>
              {infoArticles.map(article => (
                <ArticleItem
                  article={article}
                  listRule="smallTab"
                  key={article.blogId}
                />
              ))}
            </div>
            <Link to="/info" className={style.more}>
              <Button design="border" size="large" icon="arrow">
                {t('top.allInfo')}
              </Button>
            </Link>
          </section>
        )}
      </div>

      <VideoModal
        video={
          selectedVideoIndex === undefined
            ? undefined
            : allVideos[selectedVideoIndex]
        }
        onClose={() => setSelectedVideoIndex(undefined)}
      />
      {showRequestForm && (
        <RequestForm close={() => setShowRequestForm(false)} />
      )}
    </Layout>
  )
}

type TabContentProps = {
  children: ReactNode
  articles: Article[]
  withRank?: boolean
  link?: { to: string; text: string }
}
const TabContent = ({
  children,
  articles,
  withRank = false,
  link,
}: TabContentProps) => {
  return (
    <div className={style.tabContentItem}>
      {children}
      <div className={style.articles} data-with-rank={withRank}>
        {articles.map((article, index) => (
          <ArticleItem
            article={article}
            rank={withRank ? index + 2 : undefined}
            listRule="pc"
            key={article.blogId}
          />
        ))}
      </div>
      {link && (
        <Link to={link.to} className={style.more}>
          <Button design="border" size="large" icon="arrow">
            {link.text}
          </Button>
        </Link>
      )}
    </div>
  )
}

export const Head: HeadFC = ({ location }) => (
  <Seo pathName={location.pathname} />
)

export default IndexPage

export const query = graphql`
  {
    allParent {
      nodes {
        pageId
        path
        title
        category
        image {
          height
          url
          width
        }
        contents
        writer
        projectName
      }
    }
    allEnglishParent {
      nodes {
        pageId
        path
        title
        category
        image {
          height
          url
          width
        }
        contents
        writer
        projectName
      }
    }
    allVideo {
      nodes {
        videoId
        title
        titleEn
        category
        tags
        tagsEn
      }
    }
    allTopInfo {
      nodes {
        dailyRanking
        dailyRankingEn
        weeklyRanking
        weeklyRankingEn
        popularTags
        popularTagsEn
        searchTags
        searchTagsEn
      }
    }
  }
`
